import { api } from './api';
import { Piece } from './pieces';

export type CustomStele = {
  id?: number;
  baseIndex: number;
  boxDownWidth: number;
  boxHeight: number;
  boxLeftHeight: number;
  boxRightHeight: number;
  boxUpWidth: number;
  depth: number;
  headIndex: number;
};

export type CustomPlating = {
  id: number;
  back: number | null;
  front: number | null;
  depth: number;
  length: number;
  monumentBack: number | null;
  monumentFront: number | null;
  monumentLength: number;
  monumentWidth: number;
  key: string;
  hasSlope: boolean | null;
  orientation: string | null;
  mode: string | null;
  isRotated: boolean;
  isActive: boolean;
};

export type CustomSole = {
  id: number;
  monumentLength: number;
  monumentWidth: number;
  height: number | null;
  frontDepth: number | null;
  holeMode: string;
  hasCustomFrontDepth: boolean | null;
  holeWidth: number | null;
  holeLength: number | null;
  poseX: number | null;
  poseZ: number | null;
  orientation: string | null;
  mode: string | null;
  length: number | null;
  depth: number | null;
  key: string;
  isRotated: boolean;
  isActive: boolean;
};

export type PieceInAssembly = {
  piece?: Piece;
  stele?: CustomStele;
  plating?: CustomPlating;
  sole?: CustomSole;
  granit: { id: string; name: string; fabrication: string };
  newGeometry?: number[];
};

export const getPiecesInAssembly = async (assemblyId: number) => {
  const { data } = await api.get<PieceInAssembly[]>(`/stock/assembly/${assemblyId}`);
  return data;
};

export type GetPiece = {
  id: number;
  name: string;
  pictureUrl: string;
  pieceFabrication: {
    fabrication: string;
  }[];
  material: 'GRANIT' | 'INOX';
};

export const getPieces = async () => {
  const { data } = await api.get<GetPiece[]>(`/stock/pieces`);
  return data;
};

export type GetPieceUnderAssembly = {
  id: number;
  name: string;
  pictureUrl: string;
  pieces: GetPiece[];
};
export const getPiecesUnderAssemblies = async () => {
  const { data } = await api.get<GetPieceUnderAssembly[]>(`/stock/under-assemblies`);
  return data;
};

export type StockPieceDto = {
  pieceId: number;
  granitId: string;
  newGeometry: number[];
  granit?: { id: string; name: string; fabrication: string };
  x?: number;
  y?: number;
  z?: number;
};

export type StockSteleDto = Partial<CustomStele> & {
  granitId: string;
  granit?: { id: string; name: string; fabrication: string };
};

export type StockPlatingDto = Partial<CustomPlating> & {
  granitId: string;
  granit?: { id: string; name: string; fabrication: string };
};

export type StockSoleDto = Partial<CustomSole> & {
  granitId: string;
  granit?: { id: string; name: string; fabrication: string };
};

export type StockDto = {
  id?: string;
  quantity: number;
  name: string;
  price: number;
  assemblyId?: number;
  isAssembly?: boolean;
  stockPieces?: StockPieceDto[];
  stockSteles?: StockSteleDto[];
  stockPlatings?: StockPlatingDto[];
  stockSoles?: StockSoleDto[];
};

export const addStock = async (body: StockDto) => {
  return api.post('/stock', body);
};

export const getStocks = async () => {
  const { data } = await api.get<StockDto[]>('/stock');
  return data;
};

export const deleteStock = async (id: string) => {
  return api.delete(`/stock/${id}`);
};

export type CheckStockBodyDto = {
  stockPieces?: StockPieceDto[];
  stockSteles?: StockSteleDto[];
  stockPlatings?: StockPlatingDto[];
  stockSoles?: StockSoleDto[];
};

export type CheckStockResponseDto = {
  stockPieces: { stockId: string; price: number; pieceId: number; granitId: string }[];
  stockSteles: { stockId: string; price: number; stele: CustomStele; granitId: string }[];
  stockPlatings: { stockId: string; price: number; platingId: number; granitId: string }[];
  stockSoles: { stockId: string; price: number; soleId: number; granitId: string }[];
  assemblyStock: {
    stockPieces: {
      stockId: string;
      price: number;
      pieceId: number;
      granitId: string;
      weight: number;
    }[];
    stockSteles: { stockId: string; price: number; stele: CustomStele; granitId: string }[];
    stockPlatings: { stockId: string; price: number; platingId: number; granitId: string }[];
    stockSoles: { stockId: string; price: number; soleId: number; granitId: string }[];
  };
};

export const checkStock = async (body: CheckStockBodyDto) => {
  const { data } = await api.post<CheckStockResponseDto>(`/stock/check`, body);
  return data;
};

export const getStocksByPieceId = async (
  pieceId: number,
  dimensions: { x: number; y: number; z: number },
) => {
  const { data } = await api.get<
    {
      price: number;
      pieceId: number;
      granitId: string;
    }[]
  >(`/stock/piece/${pieceId}?x=${dimensions.x}&y=${dimensions.y}&z=${dimensions.z}`);
  return data;
};

export const getStocksByStele = async (stele: CustomStele) => {
  const { data } = await api.get<
    {
      price: number;
      stele: CustomStele;
      granitId: string;
    }[]
  >(
    `/stock/stele?baseIndex=${stele.baseIndex}&boxDownWidth=${stele.boxDownWidth}&boxHeight=${stele.boxHeight}&boxLeftHeight=${stele.boxLeftHeight}&boxRightHeight=${stele.boxRightHeight}&boxUpWidth=${stele.boxUpWidth}&headIndex=${stele.headIndex}&depth=${stele.depth}`,
  );
  return data;
};

export const updateStock = async (id: string, body: Partial<StockDto>) => {
  return api.put(`/stock/${id}`, body);
};

export const getAssemblyStockWithoutGranit = async (body: CheckStockBodyDto) => {
  const { data } = await api.post<StockDto[]>(`/stock/check/assembly`, body);
  return data;
};
